import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../services/api';
import {
  PageContainer,
  MainContent,
  Card,
  CardHeader,
  CardTitle,
  CardSubtitle,
  FormSpace,
  AlertBox,
  AlertBoxContent,
  AlertTitle
} from './steps/TeacherJourneyStyles';
import RegistrationForm from './steps/RegistrationForm';
import SchoolDetails from './steps/SchoolDetails';
import OtpVerification from './steps/OtpVerification';
import RegistrationComplete from './steps/RegistrationComplete'
import TeacherFooter from './TeacherFooter';
import SetPassword from './steps/SetPassword';
import Dashboard from './dashboard/Dashboard';
import { useUser } from '../../contexts/UserContext';

interface Student {
  _id: string;
  firstName: string;
  lastName: string;
  grade: string;
  email: string;
}

interface Class {
  _id: string;
  name: string;
  grade: string;
  students: Student[];
}

interface Notification {
  id: number;
  type: 'alert' | 'info' | 'success';
  message: string;
  time: string;
}

interface TeacherFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  schoolId: string;
  schoolName: string;
  teacherId: string;
  subjects: string[];
  bio: string;
  certificationId: string;
  password: string;
  confirmPassword: string;
}

// Mock data for development
const mockClasses: Class[] = [
  {
    _id: '1',
    name: 'Grade 4A',
    grade: '4',
    students: [
      {
        _id: '1',
        firstName: 'John',
        lastName: 'Doe',
        grade: '4',
        email: 'john.doe@example.com'
      }
    ]
  }
];

const mockNotifications: Notification[] = [
  {
    id: 1,
    type: 'info',
    message: 'Welcome to your dashboard!',
    time: '2024-03-20T10:00:00Z'
  }
];

const TeacherJourney: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [selectedSubject, setSelectedSubject] = useState('Science');
  const [selectedTerm, setSelectedTerm] = useState('Term 2');
  const { profile } = useUser();
  const [formData, setFormData] = useState<TeacherFormData>(() => {
    // Initialize form data from localStorage if available
    const savedData = localStorage.getItem('teacherRegistrationData');
    return savedData ? JSON.parse(savedData) : {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      schoolId: '',
      schoolName: '',
      teacherId: '',
      subjects: [],
      bio: '',
      certificationId: '',
      password: '',
      confirmPassword: ''
    };
  });
  const [otp, setOtp] = useState('');
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);

  const teacherData = {
    firstName: profile?.name?.split(' ')[0] || '',
    lastName: profile?.name?.split(' ').slice(1).join(' ') || '',
    email: profile?.email || '',
    schoolName: profile?.school || 'Unknown School',
    schoolId: profile?.schoolId || ''
  };

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('teacherRegistrationData', JSON.stringify(formData));
  }, [formData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(formData.firstName && formData.lastName && formData.email && formData.phone);
      case 2:
        return !!(formData.schoolId && formData.schoolName);
      case 3:
        return otp.length === 6;
      case 4:
        return !!(formData.password && formData.password.length >= 8 && formData.password === formData.confirmPassword);
      default:
        return false;
    }
  };

  const handleContinue = async () => {
    if (!validateStep(currentStep)) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      switch (currentStep) {
        case 1:
          // Store basic info locally only
          setCurrentStep(2);
          break;

        case 2:
          // Store school details locally only
          setCurrentStep(3);
          // Send OTP for verification
          await apiClient.post('/auth/send-otp', { 
            email: formData.email,
            requireUser: false 
          });
          break;

        case 3:
          // Verify OTP
          const otpResponse = await apiClient.post('/auth/verify-otp', {
            email: formData.email,
            otp: otp
          });

          if (otpResponse.data.status === 'success') {
            setCurrentStep(4);
          } else {
            setError('Invalid OTP');
          }
          break;

        case 4:
          if (!formData.teacherId) {
            setError('Teacher ID is required');
            setLoading(false);
            return;
          }

          if (!formData.password) {
            setError('Password is required');
            setLoading(false);
            return;
          }

          try {
            // Format teacher ID to match expected format (e.g., POI-25-01)
            const formattedTeacherId = formData.teacherId.toUpperCase().trim();
            
            // Format school name exactly as expected
            const formattedSchoolName = formData.schoolName.trim().toUpperCase();
            // Try without the trailing plus
            const encodedSchoolName = formattedSchoolName.replace(/\s+/g, '+');
            
            const verifyUrl = `/teacher-verification/verify-school/${formattedTeacherId}?schoolName=${encodedSchoolName}`;
            
            console.log('Verifying teacher with URL:', verifyUrl);
            console.log('Formatted school name:', formattedSchoolName);
            console.log('Encoded school name:', encodedSchoolName);
            
            const verifyResponse = await apiClient.get(verifyUrl);
            
            console.log('Verification response:', verifyResponse.data);
            
            if (!verifyResponse.data || !verifyResponse.data.success) {
              const errorMessage = verifyResponse.data?.message || 'Teacher verification failed';
              console.error('Verification failed:', errorMessage);
              setError(errorMessage);
              setLoading(false);
              return;
            }

            // Extract teacher ID from the verification response
            const teacherId = verifyResponse.data.data?.data?.teacher?.id;
            if (!teacherId) {
              console.error('Verification response structure:', JSON.stringify(verifyResponse.data, null, 2));
              setError('Teacher ID not found in verification response');
              setLoading(false);
              return;
            }

            // First update the teacher record
            const updateResponse = await apiClient.put(`/teachers/${teacherId}`, {
              password: formData.password,
              status: 'active',
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              schoolId: verifyResponse.data.data?.data?.school?.id,
              schoolName: formattedSchoolName,
              teacherId: formattedTeacherId
            });

            if (!updateResponse.data.success) {
              setError('Failed to update teacher record');
              setLoading(false);
              return;
            }

            // Get the teacher record to get the hashed password
            const teacherResponse = await apiClient.get(`/teachers/${teacherId}`);
            const hashedPassword = teacherResponse.data.data.password;

            // Then create the auth user account with the same hashed password
            const authResponse = await apiClient.post('/auth/register', {
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              password: hashedPassword, // Use the already hashed password
              role: 'teacher',
              schoolId: verifyResponse.data.data?.data?.school?.id,
              teacherId: formattedTeacherId,
              bio: formData.bio,
              expertise: formData.subjects.join(', '),
              biometrics: {
                rawData: `BIO-${Date.now()}`
              }
            });

            // Check for successful registration (201 status code)
            if (authResponse.status !== 201) {
              setError('Failed to create auth account');
              setLoading(false);
              return;
            }

            // Store the teacher ID in localStorage for the dashboard to use
            localStorage.setItem('teacherId', teacherId);

            // Clear local storage
            localStorage.removeItem('teacherRegistrationData');
            setRegistrationComplete(true);
            setCurrentStep(5);

            // Wait a moment to ensure the database has updated
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Now attempt to login
            try {
              const loginResponse = await apiClient.post('/auth/login', {
                email: formData.email,
                password: formData.password,
                captchaToken: 'auto-login',
                role: 'teacher'
              });

              if (loginResponse.data.success) {
                const { token, user } = loginResponse.data.data;
                // Update the user object with the correct teacher ID
                const updatedUser = {
                  ...user,
                  teacherId: teacherId // Ensure the teacher ID is included
                };
                await login(formData.email, formData.password, 'auto-login', 'teacher');
                navigate('/teacher/dashboard');
              }
            } catch (loginErr) {
              console.error('Auto-login error:', loginErr);
              // Don't set error here, just let them see the success page and login manually
            }
          } catch (err: any) {
            console.error('Registration error:', err);
            const errorMessage = err.response?.data?.message || 'Failed to complete registration';
            console.error('Error details:', errorMessage);
            setError(errorMessage);
            setLoading(false);
          }
          break;
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleDashboardRedirect = () => {
    // Navigate to login page instead of showing dashboard
    navigate('/auth/login');
  };

  const handleSelectClass = (classData: Class) => {
    setSelectedClass(classData);
  };

  if (showDashboard) {
    return (
      <Dashboard 
        teacherData={teacherData}
        notifications={mockNotifications}
        selectedTerm={selectedTerm}
        onSelectClass={handleSelectClass}
      />
    );
  }

  return (
    <PageContainer>
      
      
      <MainContent>
        {error && (
          <AlertBox>
            <AlertBoxContent>
              <AlertTitle>{error}</AlertTitle>
            </AlertBoxContent>
          </AlertBox>
        )}
        
        {registrationComplete ? (
          <RegistrationComplete 
            onContinue={handleDashboardRedirect}
            teacherData={{
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              schoolName: formData.schoolName,
              schoolId: formData.schoolId,
              teacherId: formData.teacherId
            }}
          />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Teacher Registration</CardTitle>
              <CardSubtitle>Step {currentStep} of 5</CardSubtitle>
            </CardHeader>
            
            <FormSpace>
              {currentStep === 1 && (
                <RegistrationForm
                  formData={formData}
                  onInputChange={handleInputChange}
                  onContinue={handleContinue}
                  loading={loading}
                />
              )}
              {currentStep === 2 && (
                <SchoolDetails
                  formData={formData}
                  onInputChange={handleInputChange}
                  onContinue={handleContinue}
                  loading={loading}
                />
              )}
              {currentStep === 3 && (
                <OtpVerification
                  email={formData.email}
                  otp={otp}
                  onOtpChange={(e) => {
                    e.preventDefault();
                    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                    setOtp(value);
                    setError(null);
                  }}
                  onVerify={() => {
                    if (otp.length === 6 && !loading) {
                      handleContinue();
                    }
                  }}
                  onResend={async () => {
                    if (!loading) {
                      try {
                        await apiClient.post('/auth/send-otp', { 
                          email: formData.email,
                          requireUser: false 
                        });
                      } catch (error) {
                        setError('Failed to resend OTP. Please try again.');
                      }
                    }
                  }}
                  loading={loading}
                />
              )}
              {currentStep === 4 && (
                <SetPassword
                  onPasswordSet={(password) => {
                    setFormData(prev => ({
                      ...prev,
                      password,
                      confirmPassword: password
                    }));
                    handleContinue();
                  }}
                  loading={loading}
                />
              )}
            </FormSpace>
          </Card>
        )}
      </MainContent>
      
      <TeacherFooter />
    </PageContainer>
  );
};

export default TeacherJourney; 